import { DropdownButton, Menu, Tooltip } from '@neo4j-ndl/react';
import { CodeBracketIconOutline, CommandLineIconOutline, RectangleGroupIconOutline } from '@neo4j-ndl/react/icons';
import { APP_SCOPE } from '@nx/constants';
import { type Instance } from '@nx/state';
import { useConnect, useNxNavigate } from '@nx/ui';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ExploreIconOutline } from '../components/icons';

type ConnectMenuProps = {
  instance: Instance;
  isDisabled: boolean;
  type?: 'table' | 'list';
};

export const ConnectMenu = ({ instance, type = 'list', isDisabled }: ConnectMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  const nxNavigate = useNxNavigate();
  const { doDiscoveryAndConnect } = useConnect();

  const handleClick = () => setIsMenuOpen((prevState) => !prevState);

  const handleConnect = async (app: APP_SCOPE) => {
    nxNavigate(app);
    await doDiscoveryAndConnect(instance);
  };

  return (
    <>
      <Tooltip type="simple" isDisabled={instance.availableActions.connect.enabled}>
        <Tooltip.Trigger hasButtonWrapper>
          <DropdownButton
            ref={menuRef}
            isOpen={isMenuOpen}
            onClick={handleClick}
            size={type === 'list' ? 'medium' : 'small'}
            isDisabled={!instance.availableActions.connect.enabled}
            htmlAttributes={{
              'aria-label': 'Connection menu',
            }}
          >
            Connect
          </DropdownButton>
        </Tooltip.Trigger>
        <Tooltip.Content>
          {!instance.availableActions.connect.enabled && instance.availableActions.connect.message}
        </Tooltip.Content>
      </Tooltip>
      <Menu isOpen={isMenuOpen} anchorRef={menuRef} onClose={handleClick}>
        <Menu.Items>
          <Menu.Item
            title="Query"
            icon={<CommandLineIconOutline />}
            isDisabled={isDisabled}
            onClick={() => {
              void handleConnect(APP_SCOPE.query);
            }}
          />
          <Menu.Item
            title="Explore"
            icon={<ExploreIconOutline />}
            isDisabled={isDisabled}
            onClick={() => {
              void handleConnect(APP_SCOPE.explore);
            }}
          />
          <Menu.Item
            title="Dashboards"
            icon={<RectangleGroupIconOutline />}
            isDisabled={isDisabled}
            onClick={() => {
              void handleConnect(APP_SCOPE.dashboards);
            }}
          />
          <Menu.Divider />
          <Menu.Item
            icon={<CodeBracketIconOutline />}
            title="Developer hub"
            onClick={() => {
              void navigate(`/projects/${projectId}/developer-hub`);
            }}
          />
        </Menu.Items>
      </Menu>
    </>
  );
};
